import React, { useState, useEffect, useRef } from 'react';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const WIDGET_BASE = process.env.REACT_APP_WIDGET_URL || 'https://app.websolutionusa.com/widget';

function CodeBlock({ code, onCopy, copied }) {
  return (
    <div className="relative">
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-x-auto leading-relaxed whitespace-pre-wrap break-all">
        <code>{code}</code>
      </pre>
      <button
        onClick={onCopy}
        className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white text-xs transition-all"
      >
        {copied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}

// Live interactive popup preview (runs inside the dashboard)
function PopupPreview({ embedUrl }) {
  const [open, setOpen]     = useState(false);
  const [loaded, setLoaded] = useState(false);
  const iframeRef           = useRef(null);

  const openModal = () => {
    setOpen(true);
    if (!loaded) setLoaded(true);
  };
  const closeModal = () => setOpen(false);

  useEffect(() => {
    const handler = (e) => {
      if (!e.data) return;
      if (e.data.type === 'yp-resize' && iframeRef.current) {
        iframeRef.current.style.height = Math.max(e.data.height, 520) + 40 + 'px';
      }
      if (e.data.type === 'yp-booking-complete') setTimeout(closeModal, 1500);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <div className="flex flex-col items-center gap-4 p-8 bg-surface-50 rounded-xl min-h-[260px] justify-center">
      <p className="text-xs text-ink-muted text-center">Click the button below to test the popup live</p>
      <button
        onClick={openModal}
        className="px-6 py-3 rounded-xl text-white font-semibold text-sm shadow"
        style={{ background: '#2E5BBA' }}
      >
        Book Now — Preview
      </button>
      <p className="text-xs text-ink-muted">Modal opens on this page · no external site needed</p>

      {open && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0,0,0,.65)', zIndex: 999999,
            overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          }}
        >
          <div style={{ minHeight: '100%', padding: '20px 12px', boxSizing: 'border-box', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 500, margin: 'auto', position: 'relative', transform: 'translateZ(0)' }}>
              <button
                onClick={closeModal}
                style={{ position: 'absolute', top: 10, right: 10, background: '#f1f5f9', border: 'none', width: 32, height: 32, borderRadius: '50%', fontSize: 20, lineHeight: '32px', cursor: 'pointer', zIndex: 10, color: '#333', textAlign: 'center' }}
              >×</button>
              <iframe
                ref={iframeRef}
                src={loaded ? embedUrl : 'about:blank'}
                style={{ border: 'none', width: '100%', height: 600, display: 'block', borderRadius: 16, transform: 'translateZ(0)' }}
                allow="storage-access"
                title="Widget Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function buildNewTabSnippet(embedUrl) {
  return `<!-- ── Booking Widget: Open in New Tab ──────────────────
  Paste this once before </body> on your website.
  Automatically detects ALL "Book" / "Appointment" buttons.
──────────────────────────────────────────────────── -->
<script>
(function(){
  if(window.__ypInit)return; window.__ypInit=true;
  var URL='${embedUrl}';
  function go(e){ e.preventDefault(); window.open(URL,'_blank','noopener'); }
  function attach(){
    /* 1. Prefer explicit attribute */
    var els=Array.from(document.querySelectorAll('[data-book-appointment]'));
    /* 2. Fall back to text detection */
    if(!els.length){
      els=Array.from(document.querySelectorAll('a,button,[role="button"]'))
        .filter(function(el){
          return /book|appoint/i.test((el.textContent||el.getAttribute('value')||''));
        });
    }
    els.forEach(function(el){ if(!el._yp){el._yp=1;el.addEventListener('click',go);} });
  }
  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',attach);
  } else { attach(); }
})();
</` + `script>`;
}

function buildPopupSnippet(embedUrl) {
  return `<!-- ── Booking Widget: Popup Modal ─────────────────────
  Paste this once before </body> on your website.
  Automatically detects ALL "Book" / "Appointment" buttons.
  iOS/Safari: opens in new tab (Apple blocks 3rd-party iframes).
──────────────────────────────────────────────────── -->
<script>
(function(){
  if(window.__ypInit)return; window.__ypInit=true;
  var BOOKING_URL='${embedUrl}';

  /* ── iOS / Safari detection ─────────────────────────────
     Apple ITP blocks cross-origin iframes that have not had
     direct user interaction with the origin. The only reliable
     fix for iOS Safari is to open in a new tab.              */
  var isIOS=/iPad|iPhone|iPod/.test(navigator.userAgent)&&!window.MSStream;
  var isSafari=/^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  if(isIOS||isSafari){
    function goBook(e){ e.preventDefault(); window.open(BOOKING_URL,'_blank','noopener'); }
    function attachFallback(){
      var els=Array.from(document.querySelectorAll('[data-book-appointment]'));
      if(!els.length) els=Array.from(document.querySelectorAll('a,button,[role="button"]'))
        .filter(function(el){ return /book|appoint/i.test((el.textContent||el.getAttribute('value')||'')); });
      els.forEach(function(el){ if(!el._yp){el._yp=1;el.addEventListener('click',goBook);} });
    }
    if(document.readyState==='loading'){
      document.addEventListener('DOMContentLoaded',attachFallback);
    } else { attachFallback(); }
    return; /* skip modal on iOS/Safari */
  }

  /* ── Inject modal HTML ──────────────────────────────── */
  var wrap=document.createElement('div');
  wrap.innerHTML=[
    '<div id="yp-ov" style="display:none;position:fixed;top:0;left:0;width:100%;height:100%;',
    'background:rgba(0,0,0,.6);z-index:2147483646;overflow-y:auto;-webkit-overflow-scrolling:touch;">',
    '<div style="min-height:100%;padding:16px;box-sizing:border-box;display:flex;',
    'align-items:flex-start;justify-content:center;">',
    '<div style="background:#fff;border-radius:16px;width:100%;max-width:500px;margin:auto;',
    'position:relative;-webkit-transform:translateZ(0);transform:translateZ(0);">',
    '<button id="yp-cls" style="position:absolute;top:10px;right:10px;background:#f1f5f9;',
    'border:none;width:32px;height:32px;border-radius:50%;font-size:20px;line-height:32px;',
    'cursor:pointer;z-index:10;color:#333;text-align:center;">&#215;</button>',
    '<iframe id="yp-frm" src="about:blank" allow="storage-access" title="Book Appointment"',
    ' style="border:none;width:100%;height:600px;display:block;border-radius:16px;',
    '-webkit-transform:translateZ(0);transform:translateZ(0);"></iframe>',
    '</div></div></div>',
  ].join('');
  document.body.appendChild(wrap);

  var ov=document.getElementById('yp-ov');
  var frm=document.getElementById('yp-frm');
  var sy=0, ypLoaded=false;

  function openModal(){
    sy=window.pageYOffset||0;
    /* iOS-safe scroll lock */
    document.body.style.cssText='position:fixed!important;top:-'+sy+'px!important;'+
      'left:0!important;right:0!important;width:100%!important;overflow:hidden!important;';
    ov.style.display='block';
    /* Load src AFTER modal is visible — key iOS fix */
    if(!ypLoaded){ ypLoaded=true; setTimeout(function(){ frm.src=BOOKING_URL; },80); }
  }
  function closeModal(){
    ov.style.display='none';
    document.body.style.cssText='';
    window.scrollTo(0,sy);
  }

  document.getElementById('yp-cls').addEventListener('click',closeModal);
  ov.addEventListener('click',function(e){ if(e.target===ov)closeModal(); });

  window.addEventListener('message',function(e){
    if(!e.data)return;
    if(e.data.type==='yp-resize') frm.style.height=Math.max(e.data.height,520)+40+'px';
    if(e.data.type==='yp-booking-complete') setTimeout(closeModal,1500);
  });

  /* ── Attach to booking buttons ──────────────────────── */
  function attach(){
    var els=Array.from(document.querySelectorAll('[data-book-appointment]'));
    if(!els.length) els=Array.from(document.querySelectorAll('a,button,[role="button"]'))
      .filter(function(el){ return /book|appoint/i.test((el.textContent||el.getAttribute('value')||'')); });
    els.forEach(function(el){
      if(!el._yp){ el._yp=1; el.addEventListener('click',function(e){ e.preventDefault(); openModal(); }); }
    });
  }
  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',attach);
  } else { attach(); }
})();
</` + `script>`;
}

export default function Embed() {
  const { tenant }  = useAuth();
  const slug        = tenant?.slug || 'your-slug';
  const [type, setType]   = useState('popup');
  const [copied, setCopied] = useState(false);

  const embedUrl = `${WIDGET_BASE}?tenant=${slug}`;
  const snippet  = type === 'newtab' ? buildNewTabSnippet(embedUrl) : buildPopupSnippet(embedUrl);

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-ink">Embed Widget</h1>
        <p className="text-sm text-ink-secondary mt-0.5">
          Add your booking widget to any website with one snippet.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Left: config + snippet ── */}
        <div className="space-y-4">
          <div className="card p-5 space-y-4">

            {/* Widget URL */}
            <div>
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-2">Widget URL</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-surface-50 border border-surface-border rounded-lg px-3 py-2 text-ink font-mono truncate">
                  {embedUrl}
                </code>
                <a href={embedUrl} target="_blank" rel="noreferrer" className="btn btn-secondary p-2">
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>

            {/* Embed type selector */}
            <div>
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-2">Embed type</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'popup',   label: 'Popup / Modal',     desc: 'Opens overlay on same page' },
                  { id: 'newtab',  label: 'Open in New Tab',   desc: 'Redirects to booking page' },
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => { setType(t.id); setCopied(false); }}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      type === t.id
                        ? 'border-primary bg-primary/5'
                        : 'border-surface-border hover:border-primary/40 bg-white'
                    }`}
                  >
                    <div className={`text-xs font-semibold ${type === t.id ? 'text-primary' : 'text-ink'}`}>{t.label}</div>
                    <div className="text-xs text-ink-muted mt-0.5">{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <CodeBlock code={snippet} onCopy={copy} copied={copied} />

            {/* Notes */}
            <div className="text-xs text-ink-muted space-y-1 border-t border-surface-border pt-3">
              <p className="font-medium text-ink-secondary mb-1">How to install:</p>
              <p>1. Copy the snippet above.</p>
              <p>2. Paste it <strong>once</strong> before <code className="bg-surface-100 px-1 rounded">&lt;/body&gt;</code> on your website.</p>
              <p>3. All "Book" / "Appointment" buttons are detected automatically.</p>
              {type === 'popup' && (
                <p className="mt-1 text-amber-600 font-medium">
                  ⚠ iPhone / iPad / Safari: automatically opens in new tab because Apple's ITP blocks cross-origin iframes.
                </p>
              )}
            </div>
          </div>

          {/* Button detection guide */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-ink mb-2">Button detection</h2>
            <p className="text-xs text-ink-secondary mb-2">The script finds buttons in this order:</p>
            <ol className="space-y-1.5 text-xs text-ink-secondary list-decimal list-inside">
              <li>
                Any element with <code className="bg-surface-100 px-1 rounded">data-book-appointment</code> attribute <span className="text-green-600">(recommended — most precise)</span>
              </li>
              <li>
                Any <code className="bg-surface-100 px-1 rounded">&lt;a&gt;</code>, <code className="bg-surface-100 px-1 rounded">&lt;button&gt;</code>, or <code className="bg-surface-100 px-1 rounded">[role="button"]</code> whose text contains "book" or "appoint"
              </li>
            </ol>
            <div className="mt-3 bg-slate-900 text-slate-300 rounded-lg p-3 text-xs">
              <p className="text-slate-400 mb-1">/* Optional: mark specific buttons explicitly */</p>
              <p>{'<button data-book-appointment>Book Now</button>'}</p>
            </div>
          </div>
        </div>

        {/* ── Right: live preview ── */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Live preview</p>

          {type === 'newtab' ? (
            <div className="card p-8 flex flex-col items-center gap-4 min-h-[260px] justify-center bg-surface-50">
              <div className="text-center">
                <p className="text-sm font-medium text-ink mb-1">Opens booking page in a new tab</p>
                <p className="text-xs text-ink-muted mb-4">Click the button below to test</p>
              </div>
              <a
                href={embedUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm shadow"
                style={{ background: '#2E5BBA' }}
              >
                <ExternalLink size={14} /> Book an Appointment
              </a>
              <p className="text-xs text-ink-muted text-center max-w-xs">
                On your website every button containing "book" or "appointment" will trigger this.
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <PopupPreview embedUrl={embedUrl} />
            </div>
          )}

          {type === 'popup' && (
            <div className="card p-4 bg-amber-50 border-amber-200">
              <p className="text-xs font-semibold text-amber-800 mb-1">iOS / Safari behaviour</p>
              <p className="text-xs text-amber-700">
                Apple's Intelligent Tracking Prevention (ITP) blocks iframes that load
                cross-origin content without prior user interaction with that domain.
                The embedded snippet detects iOS/Safari and automatically falls back to
                opening the booking page in a new tab — so no customer ever sees a blank popup.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
